import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import { ChartBarIcon, ClockIcon } from '@heroicons/react/solid';
import {
  Button,
  Checkbox,
  Collapse,
  Drawer,
  Dropdown,
  Tabs,
  TabsProps,
} from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import LiveIcon from 'assets/CourseIcon/LiveIcon';
import RecordIcon from 'assets/CourseIcon/RecordIcon';
import CourseArrowIcon from 'assets/Icons/CourseArrowIcon';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  Sticky as _Sticky,
  StickyContainer as _StickyContainer,
} from 'react-sticky';
import { useRecoilState, useRecoilValue } from 'recoil';

import CallbackModal from '@/components/CoursesV2/CallbackModal';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';
import SkeletonCard from '@/components/shared/SkeletonCard';

import { localCart } from '@/atoms/LocalCart';
import { localUserID, user } from '@/atoms/user';
import { addToCart, addToRecentlyViewed, getCartCount } from '@/services/Cart';
import { getCourseReaderList } from '@/services/Courses';
import { getAllCoursesCategories } from '@/services/Library/Books';
import { AddToCartProps } from '@/utils/interfaces/cart';
import { User } from '@/utils/interfaces/User';
import { formatPrice } from '@/utils/Product';

const StickyContainer: any = _StickyContainer;
const Sticky: any = _Sticky;

const { Panel } = Collapse;

interface CourseReader {
  id: string;
  title: string;
  instructor: string;
  rating: number;
  ratingCount: number;
  duration: string;
  level: string;
  isLive: boolean;
  originalPrice: number;
  currentPrice: number;
  discount: string;
  image: string;
  sessionType: string;
  isNew: boolean;
  productId: number;
  productSlug: string;
  authorUserName: string;
  description?: string;
  currencySymbol?: string;
  is_available: boolean;
  scheduleCall: boolean;
  i18n_attributes?: any;
  category?: string;
}

interface Props {
  isMyCourses?: boolean;
  ecomProducts?: any[];
}

const customExpandIconPlusMinus = (props: any) => {
  if (props.isActive) {
    return (
      <a style={{ color: 'black' }}>
        <ChevronUpIcon className='w-4 text-white ' />
      </a>
    );
  } else {
    return (
      <a style={{ color: 'black' }}>
        <ChevronDownIcon className='w-4 text-white' />
      </a>
    );
  }
};

type PriceSortKey = 'high' | 'low' | 'rating';

const priceItems: { key: PriceSortKey | 'default'; label: string }[] = [
  { key: 'default', label: 'Default' },
  { key: 'high', label: 'Price: High to Low' },
  { key: 'low', label: 'Price: Low to High' },
  { key: 'rating', label: 'Rating: High to Low' },
];
const allFilters = {
  'Video Duration': [
    { label: '0-1 Hour' },
    { label: '1-3 Hours' },
    { label: '3-6 Hours' },
    { label: '6-9 Hours' },
    { label: '9-15 Hours' },
    { label: '15-24 Hours' },
    { label: '24+ Hours' },
  ],
  Level: [
    { label: 'Beginner' },
    { label: 'Intermediate' },
    { label: 'Advanced' },
  ],
  'Session Type': [{ label: 'Live' }, { label: 'Recorded' }],
};

const sortMap: Record<string, string> = {
  high: 'price_high_to_low',
  low: 'price_low_to_high',
  rating: 'rating_high_to_low',
};

const CoursesListingPage: React.FC<Props> = ({
  isMyCourses = false,
  ecomProducts,
}) => {
  const [selectedFilters, setSelectedFilters] = useState({
    'Video Duration': [] as string[],
    Level: [] as string[],
    'Session Type': [] as string[],
    Price: [] as string[],
  });
  const [expanded, setExpanded] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<{ key: string; label: string }>({
    key: 'All',
    label: 'All',
  });
  const [courses, setCourses] = useState<CourseReader[]>([]);
  const [, setTotalCourses] = useState<number>();
  const [loadingCart, setLoadingCart] = useState({ value: false, id: 0 });
  const [, setLocalCartCount] = useRecoilState(localCart);
  const [search, setSearch] = useState('');
  const [categories, setCategories] =
    useState<{ id: string; name: string }[]>();
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState({
    value: false,
    productId: 0,
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [, setLoading] = useState(false);
  const localUserId = useRecoilValue(localUserID);

  const { ref: loaderRef, inView } = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const router = useRouter();

  const userData: User | undefined = useRecoilValue(user);

  const limit = router.query.limit?.toString() || '10';
  const screens = useBreakpoint();

  const openModal = (productId: number) =>
    setIsModalVisible({ value: true, productId: productId });
  const closeModal = () => setIsModalVisible({ value: false, productId: 0 });

  const leftContainerPadding = 'lg:pl-10 xl:pl-16 pr-6';

  const rightContainerPadding = 'px-4 md:px-8';

  const items: TabsProps['items'] = [
    {
      key: 'All',
      label: (
        <span
          className={clsx(
            activeTab.key === 'All' ? 'text-white' : 'text-grayZl-600',
            'text-sm md:text-lg font-medium px-2 md:px-4',
          )}
        >
          All
        </span>
      ),
      children: null,
    },
    ...(categories?.map((tab) => ({
      key: tab.id,
      label: (
        <span
          className={clsx(
            tab.id === activeTab.key ? 'text-white' : 'text-grayZl-600',
            'text-sm md:text-lg font-medium px-2 md:px-4',
          )}
        >
          {tab.name}
        </span>
      ),
      children: null,
    })) || []),
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  useEffect(() => {
    if (inView && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [inView, hasMore]);
  const handleGetCategories = async () => {
    try {
      const response = await getAllCoursesCategories();

      setCategories(
        response.map((category: any) => ({
          id: category?._id,
          name: category?.name,
        })),
      );
    } catch (error: any) {
      notify(formateErrorObject(error.message), 'error');
    }
  };

  useEffect(() => {
    handleGetCategories();
    setActiveTab({ key: 'All', label: 'All' });
  }, []);

  useEffect(() => {
    if (page !== 1) {
      fetchCourses(search, page);
    }
  }, [page]);

  const fetchCourses = async (
    searchKeyword?: string,
    currentPage: number = 1,
  ) => {
    try {
      setLoading(true);
      const response = await getCourseReaderList({
        page: currentPage.toString(),
        limit: '10',
        sortBy: sortMap[sortBy ?? ''] || undefined,
        duration: selectedFilters['Video Duration']?.map((item) =>
          item.replace('+', '').replace(/\s*Hours?$/, ''),
        ),
        level: selectedFilters.Level,
        keyword: searchKeyword?.trim() || undefined,
        sessionType: selectedFilters['Session Type'],
        categoriesId: activeTab.key !== 'All' ? activeTab.key : null,
      });
      const meta = response.data.meta;
      const newCourses: CourseReader[] = (response?.data?.data || []).map(
        (item: any) => {
          const matchedEcomCourse = ecomProducts?.find(
            (ecom: any) => ecom?.id == item?.form?.productId,
          );

          const is_available =
            matchedEcomCourse?.is_available &&
            matchedEcomCourse?.i18n_attributes;

          const currencySymbol = matchedEcomCourse?.currency_symbol || '₹';
          const i18n_attributes = matchedEcomCourse?.i18n_attributes || {};

          const original =
            matchedEcomCourse?.stroked_price || item?.form?.stroked_price;
          const current =
            matchedEcomCourse?.main_price || item?.form?.main_price;
          const discountPercent =
            original && current
              ? `${Math.round(((original - current) / original) * 100)}%`
              : '0%';

          return {
            id: item._id,
            title: item?.form?.bookName || '',
            instructor: item?.form?.authorName || 'Unknown Instructor',
            rating: parseFloat(item.averageRating || '0'),
            ratingCount: item.ratingCount || 0,
            duration: item?.form?.courseDuration || '',
            level: item?.form?.courseLevel || 'Beginner',
            isLive: item?.form?.sessionType === 'live',
            originalPrice: original,
            currentPrice: current,
            discount: discountPercent,
            image: item?.form?.bookImage || '',
            isNew: item.isUpcoming || false,
            sessionType: item?.form?.sessionType || '',
            productId: item?.form?.productId || 0,
            productSlug: item?.productSlug || '',
            authorUserName: item?.form?.authUser_name || '',
            description: item?.form?.shortDescription || '',
            scheduleCall: item?.form?.scheduleCall || false,
            is_available: is_available || false,
            currencySymbol: currencySymbol,
            i18n_attributes,
            category: item?.form?.tags?.[0] || (activeTab.label !== 'All' ? activeTab.label : undefined),
          };
        },
      );
      setTotalCourses(meta?.totalItems || 0);
      setCourses((prev) => [...prev, ...newCourses]);

      setHasMore(meta.currentPage < meta.totalPages);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const handler = setTimeout(() => {
      setCourses([]);
      setPage(1);
      fetchCourses(search, 1);
      setIsFilterDrawerOpen(false);
    }, 300);

    return () => clearTimeout(handler);
  }, [search, limit, selectedFilters, sortBy, activeTab]);

  const courseFilterRender = (
    <div className='pt-8 h-full '>
      <h3
        className={clsx(
          leftContainerPadding,
          'text-2xl  xl:text-[32px] my-0 font-semibold pb-5',
        )}
      >
        Filters
      </h3>
      <div className={clsx(leftContainerPadding, 'pb-6')}>
        <Dropdown
          trigger={['click']}
          menu={{
            items: priceItems,
            onClick: ({ key }) => {
              if (key === 'default') {
                setSortBy(null);
              } else {
                setSortBy(key as PriceSortKey);
              }
            },
          }}
          overlayClassName='custom-dropdown-menu'
        >
          <div className='w-full flex-col flex gap-y-1 border border-grayZl-850 bg-transparent rounded-md px-4 py-2 cursor-pointer bg-[#121212]'>
            <p className='text-xs text-grayZl-500  leading-none font-light'>
              Sort by
            </p>
            <div className='flex justify-between gap-x-2 items-center'>
              <span className='text-base truncate text-white font-medium '>
                {priceItems.find((i) => (sortBy ?? 'default') === i.key)?.label}
              </span>
              <ChevronDownIcon className='text-white text-xs w-4 -translate-y-1 h-4' />
            </div>
          </div>
        </Dropdown>
      </div>
      <Collapse
        expandIconPosition='end'
        ghost
        defaultActiveKey={['1']}
        expandIcon={(props) => customExpandIconPlusMinus(props)}
        className='text-white customCollapseCourseListing rounded-none'
        rootClassName='bg-none rounded-none'
      >
        {Object.entries(allFilters).map(([filterKey, data], index) => {
          const isDuration = filterKey === 'Video Duration';
          const selected =
            selectedFilters[filterKey as keyof typeof selectedFilters] || [];

          const handleCheckboxChange = (label: string, checked: boolean) => {
            setSelectedFilters((prev) => ({
              ...prev,
              [filterKey]: checked
                ? [...selected, label]
                : selected.filter((item) => item !== label),
            }));
          };

          const visibleItems = isDuration ? data.slice(0, 4) : data;
          const hiddenItems = isDuration ? data.slice(4) : [];

          return (
            <Panel
              key={index + 1}
              header={
                <span
                  className={clsx(
                    leftContainerPadding,
                    'text-white text-base xl:text-lg whitespace-nowrap font-semibold py-4',
                  )}
                >
                  {filterKey}
                </span>
              }
              className='border border-x-0 border-b-0 border-grayZl-850 '
            >
              <div
                className={clsx(
                  leftContainerPadding,
                  'rounded px-4 flex flex-col gap-3',
                )}
              >
                {visibleItems.map(({ label }, index) => (
                  <div
                    key={`${index}-${label}`}
                    className='flex gap-x-2 items-center'
                  >
                    <Checkbox
                      checked={selected.includes(label)}
                      onChange={(e) =>
                        handleCheckboxChange(label, e.target.checked)
                      }
                      className='text-white [&_.ant-checkbox-inner]:w-4 [&_.ant-checkbox-inner]:h-4
                      [&_.ant-checkbox-inner]:rounded [&_.ant-checkbox-inner]:border-2 
                      [&_.ant-checkbox-inner]:border-white [&_.ant-checkbox-inner]:bg-transparent 
                      [&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-[#ff5c00] 
                      [&_.ant-checkbox-checked_.ant-checkbox-inner]:border-[#ff5c00] 
                      [&_.ant-checkbox-checked_.ant-checkbox-inner::after]:border-white'
                    >
                      <div className='font-light text-sm pl-1 text-white'>
                        {label}
                      </div>
                    </Checkbox>
                  </div>
                ))}

                {/* Smooth slide animation for expanded content */}
                <AnimatePresence initial={false}>
                  {isDuration && expanded && hiddenItems.length > 0 && (
                    <motion.div
                      key='expand-section'
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className='overflow-hidden flex flex-col gap-3'
                      layout
                    >
                      {hiddenItems.map(({ label }) => (
                        <div
                          key={`${index}-${label}`}
                          className='flex gap-x-2 items-center'
                        >
                          <Checkbox
                            checked={selected.includes(label)}
                            onChange={(e) =>
                              handleCheckboxChange(label, e.target.checked)
                            }
                            className='text-white [&_.ant-checkbox-inner]:w-4 [&_.ant-checkbox-inner]:h-4
                      [&_.ant-checkbox-inner]:rounded [&_.ant-checkbox-inner]:border-2 
                      [&_.ant-checkbox-inner]:border-white [&_.ant-checkbox-inner]:bg-transparent 
                      [&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-[#ff5c00] 
                      [&_.ant-checkbox-checked_.ant-checkbox-inner]:border-[#ff5c00] 
                      [&_.ant-checkbox-checked_.ant-checkbox-inner::after]:border-white'
                          >
                            <div className='font-light text-sm pl-1 text-white'>
                              {label}
                            </div>
                          </Checkbox>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Show More / Show Less */}
                {isDuration && hiddenItems.length > 0 && (
                  <div
                    className='pt-1 text-[#FF5C00] font-semibold flex gap-x-2 cursor-pointer select-none'
                    onClick={() => setExpanded(!expanded)}
                  >
                    {!expanded ? (
                      <span className='flex gap-x-2'>
                        Show More <ChevronDownIcon className='w-4' />
                      </span>
                    ) : (
                      <span className='flex gap-x-2'>
                        Show Less <ChevronUpIcon className='w-4' />
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  const onMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    isDragging.current = true;
    scrollRef.current.style.cursor = 'grabbing';
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onMouseLeave = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  const onMouseUp = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const updateGradients = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftGradient(scrollLeft > 0);
    setShowRightGradient(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateGradients();
    el.addEventListener('scroll', updateGradients);

    return () => {
      el.removeEventListener('scroll', updateGradients);
    };
  }, []);

  const handleCartCount = async () => {
    try {
      const data = await getCartCount({});
      setLocalCartCount(data.count);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const handleAddDataToCart = async (
    payload: AddToCartProps,
    courseName?: string,
  ) => {
    try {
      setLoadingCart({ value: true, id: payload.id });

      await addToCart(payload);

      addToRecentlyViewed(payload.id, userData?.zl_uid);
      notify(`${courseName} added to cart successfully!`, 'success');
      router.push('/cart');
      handleCartCount();
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoadingCart({ value: false, id: payload.id });
    }
  };
  return (
    <div className=' text-white relative'>
      <ShouldRender check={screens.md}>
        <div
          className={clsx(
            isMyCourses && 'mt-6 md:mt-8',
            'flex flex-col justify-center items-start gap-1 md:px-10 xl:px-16 px-4  rounded',
          )}
        >
          <h2 className=' text-white text-lg md:text-2xl xl:text-[32px] font-semibold md:leading-10'>
            All the skills you need in one place
          </h2>
          <div className='text-grayZl-500  text-sm md:text-base xl:text-lg font-light md:leading-6'>
            From critical skills to technical topics, ZebraLearn supports your
            professional development.
          </div>
        </div>
      </ShouldRender>

      <div className='relative w-full'>
        {showLeftGradient && (
          <div
            className='pointer-events-none absolute left-0 top-0 h-full w-16 z-10'
            style={{
              background:
                'linear-gradient(90deg, rgba(8, 8, 8, 0.90) 0%, rgba(8, 8, 8, 0.00) 100%)',
            }}
          />
        )}
        {showRightGradient && (
          <div
            className='pointer-events-none absolute right-0 top-0 h-full w-16 z-10'
            style={{
              background:
                'linear-gradient(270deg, rgba(8, 8, 8, 0.90) 0%, rgba(8, 8, 8, 0.00) 100%)',
            }}
          />
        )}

        <div
          ref={scrollRef}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onMouseMove={onMouseMove}
          className={clsx(
            screens.lg ? leftContainerPadding : 'px-4 md:px-10',
            'flex justify-start mt-3 md:mt-6 overflow-x-auto hide-scrollbar cursor-grab active:cursor-grabbing relative',
          )}
        >
          <div className='min-w-max'>
            <Tabs
              activeKey={activeTab.key}
              onChange={(key) => {
                const selectedLabel =
                  key === 'All'
                    ? 'All'
                    : categories?.find((cat) => cat.id === key)?.name || '';
                setActiveTab({ key, label: selectedLabel });
              }}
              items={items}
              tabBarStyle={{
                borderBottom: '1px solid #333',
                marginBottom: '0px',
              }}
              animated={false}
              type='line'
              tabBarGutter={screens.md ? 20 : 12}
            />
          </div>
        </div>
      </div>

      <StickyContainer className='flex bg-[#151515]'>
        <ShouldRender check={screens.lg}>
          <div className='lg:w-3/12 min-w-[248px]'>
            <Sticky>
              {({
                style,
                isSticky,
              }: {
                style: React.CSSProperties;
                isSticky: boolean;
              }) => (
                <div
                  style={{
                    ...style,
                    paddingTop: isSticky ? '40px' : '0px',
                  }}
                >
                  {courseFilterRender}
                </div>
              )}
            </Sticky>
          </div>
        </ShouldRender>

        <div className='flex flex-col lg:border-l border-grayZl-850  justify-end w-full pt-6 md:pt-8'>
          <div
            className={clsx(
              rightContainerPadding,
              'flex justify-between items-center pb-3 md:pb-0',
            )}
          >
            <h3
              className={clsx(
                'text-lg md:text-2xl xl:text-[32px] my-0 font-semibold md:pb-[22px]',
              )}
            >
              {activeTab.label !== 'All' && 'All'} {activeTab.label} courses
            </h3>
          </div>
          <div
            className={clsx(
              rightContainerPadding,
              'items-center justify-center border-b md:pb-6 border-grayZl-850 flex pb-6 md:gap-x-12 flex-col md:flex-row gap-y-3 gap-x-4',
            )}
          >
            {/* <div className='flex flex-col-reverse md:flex-row items-center gap-y-2.5 gap-x-4 w-full'>
              <ShouldRender check={!screens.lg}>
                <div className='flex h-full gap-x-6 justify-start w-full'>
                  <Button
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className='border  h-12 md:h-14 border-grayZl-850 text-white bg-[#121212]'
                  >
                    <CourseFilterIcon />
                    Filter
                  </Button>

                  <ShouldRender check={totalCourses && !screens.md}>
                    <div className='text-grayZl-600  items-center justify-center flex whitespace-nowrap text-base lg:text-xl font-semibold leading-7'>
                      {totalCourses} Results
                    </div>
                  </ShouldRender>
                </div>
              </ShouldRender>
              <Input
                placeholder='Search Course by name'
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<CourseSearchIcon />}
                className={clsx(
                  ' min-h-12 md:h-14 rounded-md border-white text-white border-[1.5px] text-base placeholder:text-base font-light bg-transparent',
                  '[&>input::placeholder]:text-grayZl-500 ',
                )}
              />
            </div>
            <ShouldRender check={totalCourses && screens.md}>
              <div className='text-grayZl-600  items-center justify-center flex whitespace-nowrap text-base lg:text-xl font-semibold leading-7'>
                {totalCourses} Results
              </div>
            </ShouldRender> */}
          </div>
          <ShouldRender check={courses.length}>
            <motion.div
              className='flex flex-col'
              variants={containerVariants}
              initial='hidden'
              animate='visible'
            >
              {courses.map((course, index) => (
                <motion.div
                  key={`${index}-${course.id}`}
                  variants={cardVariants}
                  onClick={() => {
                    addToRecentlyViewed(course.productId, userData?.zl_uid);
                    router.push(
                      `/courses/${course.productSlug}/?id=${course.productId}`,
                    );
                  }}
                  className='flex items-start self-stretch border-b border-grayZl-850 '
                >
                  <div className='flex flex-col relative justify-center items-start gap-2.5 self-stretch py-0 px-3 md:px-6 border-r border-r-[#ffffff]/[.10]'>
                    <div className='absolute inset-0 overflow-hidden'>
                      <Image
                        src={course.image}
                        alt={course.title}
                        fill
                        className='object-cover md:p-8 lg:p-14 blur-[100px] opacity-60 [mask-image:radial-gradient(circle,white,transparent)] [-webkit-mask-image:radial-gradient(circle,white,transparent)]'
                        priority
                      />
                    </div>
                    <div className='relative w-[80px] xxxs:w-[112px] md:w-[250px] aspect-[2/1] z-0 rounded overflow-hidden'>
                      <Image
                        src={course.image}
                        alt={course.title}
                        fill
                        className='object-cover relative z-10'
                      />
                    </div>
                  </div>

                  <div className='flex flex-col items-start gap-y-1 md:gap-y-2 self-stretch px-2 xxxs:px-3 py-6 md:p-6'>
                    <div className='flex justify-center items-center gap-1 py-1 px-2 mb-1 rounded border border-grayZl-900 bg-black'>
                      {course.sessionType === 'live' ? (
                        <LiveIcon />
                      ) : (
                        <RecordIcon />
                      )}
                      <div className='w-px h-2 bg-[#282829]'></div>
                      <div className='text-white  text-xs'>
                        {course.sessionType === 'live'
                          ? 'Live'
                          : 'Recorded Session'}
                      </div>
                    </div>

                    <div className='self-stretch text-white  text-xs md:text-base font-semibold leading-4 md:leading-6 line-clamp-2'>
                      {course.title}
                    </div>

                    <div className='self-stretch text-white  text-xs md:text-sm font-light leading-5 line-clamp-2'>
                      {course.description}
                    </div>

                    <button
                      type='button'
                      onClick={(e) => {
                        e.stopPropagation();
                        typeof window !== 'undefined' &&
                          window.open(
                            `/author/${course?.authorUserName}`,
                            '_blank',
                            'noopener,noreferrer',
                          );
                      }}
                      className='text-primary text-xs md:text-sm font-medium leading-5 hover:underline'
                    >
                      {course.instructor}
                    </button>

                    <div className='flex items-start flex-wrap gap-1 self-stretch'>
                      {course.level && (
                        <div className='flex justify-center items-center gap-1 py-1 px-2 rounded border border-[#282829]'>
                          <div className='text-grayZl-500 font-light text-xs capitalize flex gap-x-1 items-center'>
                            <ChartBarIcon className='text-grayZl-500 w-3 h-3' />{' '}
                            {course.level}
                          </div>
                        </div>
                      )}
                      {course.duration && (
                        <div className='flex justify-center items-center gap-1 py-1 px-2 rounded border border-[#282829]'>
                          <div className='text-grayZl-500 font-light text-xs capitalize flex gap-x-1 items-center'>
                            <ClockIcon className='text-grayZl-500 w-3.5 h-3.5' />{' '}
                            {course.duration}
                          </div>
                        </div>
                      )}
                    </div>
                    <ShouldRender check={course.is_available}>
                      <div className='flex items-center gap-2'>
                        <div className='text-white  font-semibold leading-6'>
                          {course.currencySymbol}
                          {formatPrice(course.currentPrice)}
                        </div>
                        <div className='text-grayZl-500  leading-6 font-light line-through'>
                          {course.currencySymbol}
                          {formatPrice(course.originalPrice)}
                        </div>
                        <div className='flex items-center gap-0.5 font-light py-px px-0'>
                          <div className='text-green-400  leading-6'>
                            {course.discount}
                          </div>
                          <CourseArrowIcon />
                        </div>
                      </div>
                    </ShouldRender>

                    <div className='flex flex-wrap w-full items-start gap-2'>
                      <ButtonDefault
                        size={screens.md ? 2 : 4}
                        variant={
                          course.is_available
                            ? ButtonVariants.PRIMARY
                            : ButtonVariants.UNSTYLED
                        }
                        available={course.is_available}
                        className='flex justify-center w-full xxxs:w-auto items-center gap-2 h-8 md:h-10 px-2 xxs:px-3 md:px-6 rounded-lg  text-white text-xs md:text-sm font-semibold'
                        loading={
                          loadingCart.value &&
                          loadingCart.id === course.productId
                        }
                        disabled={
                          loadingCart.value &&
                          loadingCart.id === course.productId
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddDataToCart(
                            {
                              id: course.productId as number,
                              temp_user_id: localUserId?.toString(),
                              quantity: 1,
                              trackingData: {
                                productData: {
                                  price: +course.originalPrice,
                                  id: Number(course.productId),
                                  heading: course.title,
                                  quantity: 1,
                                  currency: course?.i18n_attributes?.currency,
                                  category: course.category,
                                },
                                userData,
                                pathname: router.pathname,
                                productType: 'digital',
                                type: 'courses',
                              },
                            },
                            course.title,
                          );
                        }}
                      >
                        Add to cart
                      </ButtonDefault>
                      <ShouldRender check={course?.scheduleCall}>
                        <Button
                          size={screens.md ? 'middle' : 'small'}
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal(course.productId as number);
                          }}
                          className='flex justify-center  w-full xxxs:w-auto items-center gap-2 h-8 md:h-10 px-2  xxs:px-3 md:px-6 rounded-lg bg-white text-black text-xs md:text-sm font-semibold'
                        >
                          Talk To Us
                        </Button>
                      </ShouldRender>
                    </div>
                  </div>
                </motion.div>
              ))}

              {hasMore && (
                <div ref={loaderRef}>
                  <SkeletonCard variant='courseListing' />
                </div>
              )}
            </motion.div>
          </ShouldRender>
          <ShouldRender check={courses.length < 1}>
            <div className=' flex flex-col min-h-[50vh] justify-center items-center gap-4 h-full  pt-0 pb-6 px-8'>
              <div className='flex flex-col items-center gap-1 '>
                <div className=' text-white text-2xl font-semibold leading-8'>
                  No course Found
                </div>
                <div className='text-[#c7c6cf] leading-6'>
                  Try changing filters
                </div>
              </div>
              <Button
                ghost
                onClick={() => {
                  setSearch('');
                  setSelectedFilters({
                    'Video Duration': [] as string[],
                    Level: [] as string[],
                    'Session Type': [] as string[],
                    Price: [] as string[],
                  });
                }}
                className='border border-grayZl-900 text-white bg-transparent'
              >
                Reset Filters
              </Button>
            </div>
          </ShouldRender>
        </div>

        <CallbackModal
          visible={isModalVisible.value}
          onClose={closeModal}
          product={isModalVisible.productId}
          zl_uid={userData?.zl_uid}
        />
        <Drawer
          title={undefined}
          placement='right'
          closable
          onClose={() => setIsFilterDrawerOpen(false)}
          open={isFilterDrawerOpen}
          className='bg-[#151515] p-6 md:p-10'
          width={450}
        >
          {courseFilterRender}
        </Drawer>
      </StickyContainer>
    </div>
  );
};

export default CoursesListingPage;
